import { useUser } from "@js/apps/common/hooks/use-user";
import { useGetManagedEmployerQuery } from "@js/apps/employer/hooks";
import { openVerifyStripeACHPaymentMethodModal } from "@js/apps/payments/components/verify-stripe-ach-payment-method";
import {
  openAuthBackupCodesModal,
  openOTPDisabledWarningModal,
  OTPAuthBackupCodesModalInstance,
} from "@js/apps/settings/components/otp-auth";
import { Snackbar } from "@js/components/snackbar";
import { isErrorWithMessage } from "@js/types/errors";
import type { PaymentMethod, StripeACHPayment } from "@js/types/payments";
import { formatErrorForSnackbar } from "@js/utils";

import {
  useCreateStripeACHPaymentMethodMutation,
  useCreateStripeCCPaymentMethodMutation,
  useDeletePaymentMethodMutation,
  useGetPaymentMethodsQuery,
  useSetAsDefaultPaymentMethodMutation,
} from "../../api";
import type { AddStripeACHOnSubmitValues } from "../../components/add-stripe-ach";
import {
  DeletePaymentMethodConfirmModalInstance,
  openDeletePaymentMethodModal,
} from "../../components/delete-payment-method-confirm-modal";
import type { CreateCCPaymentMethodStripeResult } from "../../types";

import { AddPaymentModalInstance } from "./add-payment-method-modal";

type UsePaymentContainer = {
  inOfferFlow?: boolean;
};

export const usePaymentContainer = ({ inOfferFlow }: UsePaymentContainer) => {
  const user = useUser();
  const { data: employerProfile } = useGetManagedEmployerQuery();

  const {
    can_add_payment_method: canAddPaymentMethod = undefined,
    cannot_add_payment_method_reason: cannotAddPaymentMethodReason = "",
  } = employerProfile || {};

  const { data: paymentMethods, isFetching: fetchingPaymentMethods } =
    useGetPaymentMethodsQuery();

  const [deletePayment] = useDeletePaymentMethodMutation();
  const [setAsDefaultPayment] = useSetAsDefaultPaymentMethodMutation();
  const [createStripeCCMethod, { isLoading: creatingCCMethod }] =
    useCreateStripeCCPaymentMethodMutation();
  const [createStripeACHMethod, { isLoading: creatingACHMethod }] =
    useCreateStripeACHPaymentMethodMutation();

  const creatingPaymentMethod = creatingACHMethod || creatingCCMethod;

  const onPaymentMethodDelete = (
    deletePaymentMethod: (paymentMethod: PaymentMethod) => Promise<void>,
    paymentMethod: PaymentMethod,
  ) => {
    openDeletePaymentMethodModal(() =>
      deletePaymentMethod(paymentMethod)
        .catch((error) => {
          if (isErrorWithMessage(error.data)) {
            const errors = error.data._error;
            Snackbar.error(formatErrorForSnackbar(errors));
            return;
          }

          Snackbar.error("Failed to delete payment method.");
        })
        .finally(DeletePaymentMethodConfirmModalInstance.close),
    );
  };

  const deletePaymentMethod = async (paymentMethod: PaymentMethod) => {
    return deletePayment(paymentMethod.id).unwrap();
  };

  const onVerifyStripeACHPaymentMethod = (paymentMethod: StripeACHPayment) => {
    openVerifyStripeACHPaymentMethodModal({
      paymentMethod,
    });
  };

  const createStripePaymentMethod = (
    stripePaymentMethod: CreateCCPaymentMethodStripeResult,
  ) => {
    if (!stripePaymentMethod.card) return;

    return createStripeCCMethod({
      payment_method_id: stripePaymentMethod.id,
      last4: stripePaymentMethod.card.last4,
      brand: stripePaymentMethod.card.brand,
      exp_month: stripePaymentMethod.card.exp_month,
      exp_year: stripePaymentMethod.card.exp_year,
    })
      .unwrap()
      .catch((error) => {
        Snackbar.error(
          error.data._error || "Failed to add payment method, please try again",
        );
      });
  };

  const createStripeACHPaymentMethod = async (
    stripePaymentMethod: AddStripeACHOnSubmitValues,
  ) => {
    return createStripeACHMethod({
      data: {
        ...stripePaymentMethod,
        type: "us_bank_account",
      },
      in_offer_flow: inOfferFlow,
    }).unwrap();
  };

  const setAsDefaultPaymentMethod = async (paymentMethod: PaymentMethod) => {
    try {
      return await setAsDefaultPayment(paymentMethod.id).unwrap();
    } catch {
      Snackbar.error("Failed to set payment method as default.");
    }
  };

  const OnSuccessOTPEnable = (backupCodes: string[]) => {
    openAuthBackupCodesModal(backupCodes, {
      onClickContinueAction: () => {
        OTPAuthBackupCodesModalInstance.close();
        AddPaymentModalInstance.open();
      },
    });
  };

  const addPaymentMethod = () =>
    user?.is_otp_enabled || user?.is_passkey_enabled
      ? AddPaymentModalInstance.open()
      : openOTPDisabledWarningModal({
          onSuccessAction: OnSuccessOTPEnable,
          onPasskeySuccessAction: () => AddPaymentModalInstance.open(),
        });

  return {
    fetchingPaymentMethods,
    paymentMethods: paymentMethods || [],
    creatingPaymentMethod,
    onPaymentMethodDelete,
    onVerifyStripeACHPaymentMethod,
    createStripePaymentMethod,
    createStripeACHPaymentMethod,
    deletePaymentMethod,
    setAsDefaultPaymentMethod,
    addPaymentMethod,
    canAddPaymentMethod,
    cannotAddPaymentMethodReason,
  };
};
